import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpenCheck, Check, CircleSlash, Pencil, Search, X } from "lucide-react";
import { toast } from "sonner";
import BackButton from "components/ui/back-button";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import {
  createKnowledgeEntry,
  deleteKnowledgeEntry,
  getOrganizationInfo,
  listInboundPractices,
  listKnowledgeEntries,
  OrganizationInfo,
  saveOrganizationInfo,
  updateKnowledgeEntry,
} from "./inboundCallsApi";
import { KnowledgeScope } from "./knowledgeBankStore";
import { KnowledgeEntry } from "./types";
import KnowledgeTierSection from "./KnowledgeTierSection";
import { KNOWLEDGE_BANK_PATH } from "./paths";

export function knowledgeEntryPath(entryId: string): string {
  return `${KNOWLEDGE_BANK_PATH}?entry=${entryId}`;
}

const matchesEntry = (entry: KnowledgeEntry, query: string): boolean => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const corpus = `${entry.id} ${entry.question} ${entry.answer}`.toLowerCase();
  return terms.every((term) => corpus.includes(term));
};

/**
 * The readable, editable knowledge bank: every answer the assistant may give,
 * the description of each group, and the topics deliberately left uncovered.
 * Citations on a call link here, so any answer attributed to the bank can be
 * checked against the entry it came from.
 */
function KnowledgeBankPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [entries, setEntries] = useState<KnowledgeEntry[] | null>(null);
  const [practices, setPractices] = useState<{ id: string; name: string }[]>([]);
  const [orgInfo, setOrgInfo] = useState<OrganizationInfo | null>(null);
  const [sessionAddedIds, setSessionAddedIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [draftNote, setDraftNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listKnowledgeEntries(),
      listInboundPractices(),
      getOrganizationInfo(),
    ])
      .then(([loadedEntries, loadedPractices, loadedInfo]) => {
        if (!cancelled) {
          setEntries(loadedEntries);
          setPractices(loadedPractices);
          setOrgInfo(loadedInfo);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEntries([]);
          setOrgInfo({
            orgSummary: "",
            practiceSummaries: {},
            notCoveredNote: "",
          });
          toast.error("Could not load the knowledge bank.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const highlightedId = searchParams.get("entry");

  useEffect(() => {
    if (highlightedId === null || entries === null) {
      return;
    }
    document.getElementById(highlightedId)?.scrollIntoView?.({
      block: "center",
    });
  }, [highlightedId, entries]);

  const scopes: Array<{
    scope: KnowledgeScope;
    slug: string;
    title: string;
    saveTargetLabel: string;
  }> = useMemo(
    () => [
      {
        scope: { tier: "ORG" },
        slug: "org",
        title: "All locations",
        saveTargetLabel: "all locations",
      },
      ...practices.map((practice) => ({
        scope: { tier: "PRACTICE" as const, practiceId: practice.id },
        slug: practice.id,
        title: `${practice.name} only`,
        saveTargetLabel: practice.name,
      })),
    ],
    [practices]
  );

  if (entries === null || orgInfo === null) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-600">Loading knowledge bank…</p>
      </div>
    );
  }

  const entriesFor = (scope: KnowledgeScope) =>
    entries.filter((entry) =>
      scope.tier === "ORG"
        ? entry.tier === "ORG"
        : entry.tier === "PRACTICE" && entry.practiceId === scope.practiceId
    );
  const summaryFor = (scope: KnowledgeScope) =>
    scope.tier === "ORG"
      ? orgInfo.orgSummary
      : orgInfo.practiceSummaries[scope.practiceId] ?? "";

  const saveSummary = (scope: KnowledgeScope, summary: string) => {
    const next: OrganizationInfo =
      scope.tier === "ORG"
        ? { ...orgInfo, orgSummary: summary }
        : {
            ...orgInfo,
            practiceSummaries: {
              ...orgInfo.practiceSummaries,
              [scope.practiceId]: summary,
            },
          };
    setOrgInfo(next);
    saveOrganizationInfo(next).catch(() => {
      toast.error("Could not save that description.");
    });
  };

  // An empty note is a valid save: it reads as "nothing noted".
  const saveNote = () => {
    const next: OrganizationInfo = { ...orgInfo, notCoveredNote: draftNote.trim() };
    setOrgInfo(next);
    setIsEditingNote(false);
    saveOrganizationInfo(next).catch(() => {
      toast.error("Could not save that note.");
    });
  };

  const addEntry = (scope: KnowledgeScope, question: string, answer: string) => {
    createKnowledgeEntry({
      question,
      answer,
      ...(scope.tier === "PRACTICE" ? { practiceId: scope.practiceId } : {}),
    })
      .then((created) => {
        setEntries((current) => [...(current ?? []), created]);
        setSessionAddedIds((current) => new Set(current).add(created.id));
      })
      .catch(() => {
        toast.error("Could not save that answer.");
      });
  };

  const removeEntry = (entryId: string) => {
    const previous = entries;
    setEntries(entries.filter((entry) => entry.id !== entryId));
    deleteKnowledgeEntry(entryId).catch(() => {
      setEntries(previous);
      toast.error("Could not remove that answer.");
    });
  };

  const editEntry = (entryId: string, question: string, answer: string) => {
    const current = entries.find((entry) => entry.id === entryId);
    if (!current) {
      return;
    }
    updateKnowledgeEntry({
      id: entryId,
      question,
      answer,
      ...(current.tier === "PRACTICE" ? { practiceId: current.practiceId } : {}),
    })
      .then((updated) => {
        setEntries((list) =>
          (list ?? []).map((entry) => (entry.id === updated.id ? updated : entry))
        );
      })
      .catch(() => {
        toast.error("Could not save that answer.");
      });
  };

  const isSearching = query.trim() !== "";
  const sections = scopes.map((section) => {
    const all = entriesFor(section.scope);
    return {
      ...section,
      summary: summaryFor(section.scope),
      totalCount: all.length,
      entries: isSearching
        ? all.filter((entry) => matchesEntry(entry, query))
        : all,
    };
  });

  const matchCount = sections.reduce(
    (sum, section) => sum + section.entries.length,
    0
  );
  const orgCount = sections[0].totalCount;
  const overlayCount = entries.length - orgCount;

  return (
    <div className="w-full bg-background">
      <div className="border-b-2 border-gray-100 px-4 py-2 sticky top-0 bg-white z-10 flex items-center justify-between gap-2">
        <BackButton onClick={() => navigate("/dashboard/inbound_calls")} />
      </div>

      <div className="p-4 space-y-4 max-w-4xl">
        <div>
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
            <BookOpenCheck className="h-5 w-5 text-gray-500" />
            Knowledge bank
          </h2>
          <p className="text-xs text-gray-500 mt-1" data-testid="kb-counts">
            {entries.length} answers in total — {orgCount} used at all
            locations and {overlayCount} at one clinic only. If a caller asks
            anything that is not here, the assistant takes a message instead
            of guessing.
          </p>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            data-testid="kb-search"
            aria-label="Search knowledge bank"
            placeholder="Search questions and answers…"
            className="h-8 pl-8 pr-8 text-xs"
          />
          {query !== "" && (
            <button
              type="button"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              onClick={() => setQuery("")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {isSearching && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="kb-match-count"
          >
            {matchCount} of {entries.length} answers match “{query.trim()}
            ”.
          </p>
        )}

        {sections.map((section) => (
          <KnowledgeTierSection
            key={section.slug}
            scope={section.scope}
            slug={section.slug}
            title={section.title}
            saveTargetLabel={section.saveTargetLabel}
            summary={section.summary}
            entries={section.entries}
            totalCount={section.totalCount}
            isSearching={isSearching}
            highlightedId={highlightedId}
            sessionAddedIds={sessionAddedIds}
            onAdd={(question, answer) => addEntry(section.scope, question, answer)}
            onEdit={editEntry}
            onRemove={removeEntry}
            onSaveSummary={(summary) => saveSummary(section.scope, summary)}
          />
        ))}

        <Card className="gap-3 py-4 border-amber-200 bg-amber-50">
          <CardHeader className="px-4">
            <CardTitle className="text-sm flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <CircleSlash className="h-4 w-4 text-amber-700" />
                Not covered by this line
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                data-testid="kb-edit-note"
                onClick={() => {
                  setDraftNote(orgInfo.notCoveredNote);
                  setIsEditingNote((current) => !current);
                }}
              >
                <Pencil className="h-3 w-3 mr-1" />
                Edit note
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4" data-testid="kb-gaps">
            {isEditingNote ? (
              <div className="space-y-2">
                <p className="text-[10px] text-gray-600">
                  Topics this line should not answer, such as coverage under a specific plan,
                  balances, or anything clinical. The assistant takes a message for these instead.
                </p>
                <Textarea
                  value={draftNote}
                  onChange={(event) => setDraftNote(event.target.value)}
                  data-testid="kb-note-input"
                  aria-label="Not covered by this line"
                  className="text-xs min-h-20 bg-white"
                />
                <div className="flex items-center gap-1">
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    data-testid="kb-save-note"
                    onClick={saveNote}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Save note
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setIsEditingNote(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-800">
                {orgInfo.notCoveredNote ||
                  "Nothing noted. The assistant still takes a message for anything that is not in the bank."}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default KnowledgeBankPage;
